import React, { ReactNode, useEffect } from 'react'
import { isEmpty } from 'ramda'
import color from 'color'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'

import { cn } from 'design-system'
import { brandColorSelector } from 'src/store/selectors/network'
import ReduxModal from 'src/components/generics/modal/redux'

const contentOuterId = 'yd-content-outer'
const mainId = 'yd-content-main'

const lightColorSelector = createSelector(
  (c?: string) => c,
  (c?: string) => color(c).lightness(97.5).hex()
)

const lighterColorSelector = createSelector(
  (c?: string) => c,
  (c?: string) => color(c).lightness(98.5).hex()
)

/**
 * most basic layout
 * sets document title,
 * applies branded background,
 * uses a11y-compatible <main />,
 * adds floating Wix '?' button;
 * used by index page, user/settings, user/[id]/caps
 */
export default function BaseLayout({
  title,
  outerClassName = '',
  children
}: {
  title?: string
  outerClassName?: string
  children: ReactNode
}) {
  const brandColor = useSelector(brandColorSelector)

  useEffect(() => {
    document.title = `${title ? `${title} - ` : ''}Yellowdig`
  }, [title])

  return (
    <>
      <div id={contentOuterId} className={cn('cf h-full', outerClassName)}>
        <div
          className="yd-gradient"
          role="presentation"
          aria-hidden="true"
          style={
            brandColor && !isEmpty(brandColor)
              ? {
                  backgroundImage: `linear-gradient(-35deg, ${lighterColorSelector(
                    brandColor
                  )}, ${lightColorSelector(brandColor)})`
                }
              : undefined
          }
        />
        <a
          href={`#${mainId}`}
          className="sr-only--focusable sr-only fixed z-50">
          Skip to main content
        </a>
        <main className="h-full" id={mainId}>
          {children}
        </main>
      </div>
      <ReduxModal />
    </>
  )
}
